import { useQuery } from '@tanstack/react-query';
import type { UserProfile } from '@/types/api';
import { allocatorOidcIsReady } from '@/lib/auth';
import {
  acceptAuthenticatedSession,
  apiRequest,
  clearAuthenticatedBrowserState,
} from '@/lib/queryClient';

interface AuthenticatedSession {
  user: UserProfile;
  csrfToken: string;
  sessionEpoch: string;
}

const OPAQUE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43,128}$/;
const SESSION_EPOCH_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}:[0-9]+$/;

function isUserProfile(value: unknown): value is UserProfile {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  const expectedFields = ['id', 'name', 'role', 'username'];
  if (Object.keys(candidate).sort().join(',') !== expectedFields.join(',')) return false;
  return expectedFields.every(
    (field) => typeof candidate[field] === 'string' && candidate[field].trim().length > 0,
  );
}

function parseAuthenticatedSession(value: unknown): AuthenticatedSession | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Record<string, unknown>;
  if (Object.keys(candidate).sort().join(',') !== 'csrfToken,sessionEpoch,user') return null;
  if (
    !isUserProfile(candidate.user) ||
    typeof candidate.csrfToken !== 'string' ||
    !OPAQUE_TOKEN_PATTERN.test(candidate.csrfToken) ||
    typeof candidate.sessionEpoch !== 'string' ||
    !SESSION_EPOCH_PATTERN.test(candidate.sessionEpoch)
  ) {
    return null;
  }
  return {
    user: candidate.user,
    csrfToken: candidate.csrfToken,
    sessionEpoch: candidate.sessionEpoch,
  };
}

export function useCurrentUser() {
  return useQuery<UserProfile | null>({
    queryKey: ['/api/auth/user'],
    queryFn: async () => {
      if (!(await allocatorOidcIsReady())) {
        clearAuthenticatedBrowserState();
        return null;
      }
      const session = parseAuthenticatedSession(await apiRequest('GET', '/api/auth/session'));
      if (
        !session ||
        !acceptAuthenticatedSession(session.user, session.csrfToken, session.sessionEpoch)
      ) {
        clearAuthenticatedBrowserState();
        return null;
      }
      return session.user;
    },
    retry: false,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    gcTime: 10 * 60 * 1000,
  });
}

export function getUserInitials(user?: Pick<UserProfile, 'name' | 'username'> | null) {
  const source = user?.name?.trim() || user?.username?.trim() || 'QuantForge User';
  const parts = source
    .replace(/@.*/, '')
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2);

  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('') || 'QF';
}
